// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { trackingNumberDetailsSchema } from '@/constants/shipping-carrier';
import { currencySchema } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { Shipment, shipmentSchema } from '@/shipment/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for validating the response from the update shipment API endpoint.
 */
const updateShipmentResponseSchema = z.object({
  data: shipmentSchema,
  metadata: responseMetadataSchema,
});

/**
 * Schema for shipment items in the request.
 * Uses unified itemId for both parts and products.
 */
const shipmentItemSchema = z.object({
  itemId: z.string().uuid(),
  quantity: z.number().int().positive().nullable(),
  externalReference: z.string().optional().nullable(),
  serials: z.array(z.string()).optional(),
});

/**
 * Schema for received items in the request.
 * Uses unified itemId for both parts and products.
 */
const receiveItemSchema = z.object({
  itemId: z.string().uuid(),
  quantity: z.number().int().min(0).nullable(),
  externalReference: z.string().optional().nullable(),
  serials: z.array(z.string()).optional(),
});

/**
 * Schema for validating the update shipment request.
 */
export const updateShipmentSchema = z.object({
  purchaseOrderNumber: z.string().optional().nullable(),
  returnOrderNumber: z.string().optional().nullable(),
  fulfillmentOrderNumber: z.string().optional().nullable(),
  trackingNumberDetails: trackingNumberDetailsSchema.nullable().optional(),
  fromLocationId: z.string().uuid().optional().nullable(),
  toLocationId: z.string().optional().nullable(),
  items: z.array(shipmentItemSchema).optional().nullable(),
  shippedAt: z.string().optional().nullable(),
  shippedAtTimezone: z.string().optional().nullable(),
  expectedShippedOn: dateSchema.optional().nullable(),
  expectedDeliveredOn: dateSchema.optional().nullable(),
  deliveredAt: z.string().optional().nullable(),
  deliveredAtTimezone: z.string().optional().nullable(),
  shipmentNote: z.string().optional().nullable(),
  shippingCost: currencySchema.nullable().optional(),
  deliveryNote: z.string().optional().nullable(),
  receivedItems: z.array(receiveItemSchema).optional().nullable(),
  receiptNote: z.string().optional().nullable(),
});

/**
 * Type definition for the update shipment request.
 */
export type UpdateShipmentRequest = z.infer<typeof updateShipmentSchema>;

/**
 * Updates an existing shipment with the specified details.
 *
 * @param shipmentKey - The key of the shipment to update
 * @param request - The shipment details to update
 * @returns The updated shipment
 */
export async function updateShipment(
  shipmentKey: string,
  request: UpdateShipmentRequest
): Promise<Shipment> {
  const url = `/v1/shipment/${shipmentKey}`;
  const response = await api.patch(url, {
    ...request,
    expectedShippedOn: serializeDateForAPI(request.expectedShippedOn),
    expectedDeliveredOn: serializeDateForAPI(request.expectedDeliveredOn),
    shippingCost: serializeOptionalCurrencyForAPI(request.shippingCost),
  });

  const { data } = await parseResponse(
    updateShipmentResponseSchema,
    response.data,
    {
      schemaName: 'UpdateShipment',
      endpoint: url,
    }
  );
  return data;
}
